import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import realCasesService from '../services/realCasesService';

interface Scenario {
  id: string;
  title: string;
  type: string;
  description: string;
  content: any;
  differences?: any[];
  caseWarning?: string;
  preventionTips?: string[];
}

const ScenarioPage: React.FC = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams<{ scenarioId?: string }>();
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedDifferences, setSelectedDifferences] = useState<Set<string>>(new Set());
  const [score, setScore] = useState(0);
  const [scenarioIds, setScenarioIds] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      await realCasesService.loadRealCases();
      if (cancelled) return;
      setScenarioIds(realCasesService.getScenarioIds());
      if (scenarioId) {
        const scenarioData = realCasesService.getScenarioById(scenarioId);
        if (scenarioData) {
          setScenario(scenarioData);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scenarioId]);

  const handleDifferenceClick = (point: string) => {
    const newSelected = new Set(selectedDifferences);
    if (newSelected.has(point)) {
      newSelected.delete(point);
    } else {
      newSelected.add(point);
    }
    setSelectedDifferences(newSelected);
  };

  const handleCheckResults = () => {
    if (scenario?.differences) {
      let correct = 0;
      scenario.differences.forEach(diff => {
        if (selectedDifferences.has(diff.point)) {
          correct++;
        }
      });
      setScore(correct);
      setShowResults(true);
    }
  };

  if (!scenario) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ocean-50 to-white p-6">
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            {scenarioIds.length > 0 ? '未找到该情景' : '情景加载中...'}
          </p>
          <button
            onClick={() => navigate('/scenario/flightDelay')}
            className="btn-primary px-6 py-3 rounded-lg"
          >
            进入情景模拟
          </button>
        </div>
      </div>
    );
  }

  if (scenario.type === '对比识别') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean-50 to-white p-4">
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3 mb-6">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              返回
            </button>
            <h1 className="text-xl font-bold">{scenario.title}</h1>
            {scenarioIds.length > 1 && (
              <div className="ml-auto flex items-center gap-3 text-sm">
                <span className="text-gray-400">切换情景：</span>
                {scenarioIds.map(id => {
                  const item = realCasesService.getScenarioById(id) as Scenario | null;
                  if (!item) return null;
                  return (
                    <button
                      key={id}
                      onClick={() => navigate(`/scenario/${id}`)}
                      className={
                        id === scenarioId
                          ? 'text-ocean-600 font-semibold'
                          : 'text-gray-600 hover:text-gray-900'
                      }
                    >
                      {item.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </header>

        <main className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <p className="text-gray-700 mb-4">{scenario.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* 真实短信 */}
            <div className="bg-green-50 rounded-xl p-6">
              <h2 className="text-lg font-bold mb-4 text-green-800">✅ 真实短信</h2>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center mb-2">
                  <span className="text-sm text-gray-500">发件人：</span>
                  <span className="ml-2 font-medium">{scenario.content.realSMS.senderName}</span>
                  <span className="ml-2 text-xs text-gray-400">({scenario.content.realSMS.sender})</span>
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {scenario.content.realSMS.message}
                </div>
              </div>
            </div>

            {/* 诈骗短信 */}
            <div className="bg-red-50 rounded-xl p-6">
              <h2 className="text-lg font-bold mb-4 text-red-800">❓ 疑似短信</h2>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center mb-2">
                  <span className="text-sm text-gray-500">发件人：</span>
                  <span className="ml-2 font-medium">{scenario.content.fakeSMS.senderName}</span>
                  <span className="ml-2 text-xs text-gray-400">({scenario.content.fakeSMS.sender})</span>
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {scenario.content.fakeSMS.message}
                </div>
              </div>
            </div>
          </div>

          {/* 差异点对比 */}
          {!showResults && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h3 className="text-lg font-bold mb-4">找出可疑点（点击选择）</h3>
              <div className="space-y-3">
                {scenario.differences?.map((diff, index) => (
                  <div
                    key={index}
                    onClick={() => handleDifferenceClick(diff.point)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                      selectedDifferences.has(diff.point)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{diff.point}</h4>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        selectedDifferences.has(diff.point)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedDifferences.has(diff.point) ? '已选择' : '点击选择'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                      <div>
                        <span className="text-green-600">真实：</span>
                        <span className="ml-1">{diff.real}</span>
                      </div>
                      <div>
                        <span className="text-red-600">可疑：</span>
                        <span className="ml-1">{diff.fake}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleCheckResults}
                disabled={selectedDifferences.size === 0}
                className="mt-6 w-full bg-ocean-600 text-white py-3 rounded-lg hover:bg-ocean-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
              >
                查看结果
              </button>
            </div>
          )}

          {/* 结果展示 */}
          {showResults && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">
                  识别得分：{score} / {scenario.differences?.length}
                </h3>
                <p className="text-gray-600">
                  {score === scenario.differences?.length
                    ? '完美！你已经掌握了识别技巧'
                    : '继续努力，仔细观察差异点'}
                </p>
              </div>

              <div className="space-y-3">
                {scenario.differences?.map((diff, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      selectedDifferences.has(diff.point)
                        ? 'border-green-500 bg-green-50'
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="flex items-start">
                      {selectedDifferences.has(diff.point) ? (
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium mb-1">{diff.point}</h4>
                        <p className="text-sm text-gray-600">{diff.explanation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => {
                  setShowResults(false);
                  setSelectedDifferences(new Set());
                  setScore(0);
                }}
                className="mt-6 w-full bg-gray-600 text-white py-3 rounded-lg hover:bg-gray-700 transition font-semibold"
              >
                重新测试
              </button>
            </div>
          )}

          {/* 案例警示 */}
          {scenario.caseWarning && (
            <div className="bg-yellow-50 rounded-xl shadow-lg p-6 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-bold text-yellow-800 mb-2">真实案例警示</h3>
                  <p className="text-gray-700">{scenario.caseWarning}</p>
                </div>
              </div>
            </div>
          )}

          {/* 防范要点 */}
          {scenario.preventionTips && (
            <div className="bg-blue-50 rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-blue-800 mb-3">💡 防范要点</h3>
              <ul className="space-y-2">
                {scenario.preventionTips.map((tip, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    <span className="text-gray-700">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </main>
      </div>
    );
  }

  // 其他类型情景的渲染...
  return <div>该情景类型正在开发中...</div>;
};

export default ScenarioPage;